<div class="frontend-settings d-sm-flex flex-column" [formGroup]="frontendForm">
    <h3>Chat Window Settings</h3>

    <mat-accordion>

        <mat-expansion-panel [formGroup]="localizationForm" [expanded]="expandLocalizationPanel">
            <mat-expansion-panel-header>
                <mat-panel-title>
                    Quick Starts &amp; languages
                </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="general-settings d-sm-flex flex-column">
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Default locale</mat-label>
                    <mat-select formControlName="defaultLocale" placeholder="Select default locale">
                        @for (option of localeOptions; track option) {
                            <mat-option [value]="option.value">{{ option.display }}</mat-option>
                        }
                    </mat-select>
                    <mat-hint>Legacy cards only. Governed v1 cards use the authoritative retail or automotive card kind.</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Supported locales</mat-label>
                    <mat-select formControlName="supportedLocales" multiple placeholder="Select supported locales">
                        @for (option of localeOptions; track option) {
                            <mat-option [value]="option.value">{{ option.display }}</mat-option>
                        }
                    </mat-select>
                    <mat-hint>Default locale is included automatically when saving.</mat-hint>
                </mat-form-field>

                <mat-slide-toggle formControlName="autoGenerate">Generate stale translations with GPT-5.5</mat-slide-toggle>

                @if (allowGenerativeActions) {
                    <div class="chat-avatar-actions">
                        <app-disabled-button-reason [disabled]="generateLocalizationDisabled"
                                                    [reason]="generateLocalizationDisabledReason">
                            <button mat-stroked-button color="primary" type="button"
                                    [disabled]="generateLocalizationDisabled"
                                    (click)="generateLocalization.emit()">
                                {{ isLocalizing ? 'Generating...' : 'Generate translations' }}
                            </button>
                        </app-disabled-button-reason>
                    </div>
                }

                <app-quick-start-editor
                    [sampleMessages]="defaultSampleMessages"
                    [chatbotId]="chatbotId"
                    [localeLabel]="defaultLocaleLabel"
                    [allowSuggestions]="true"
                    [allowGenerativeActions]="allowGenerativeActions">
                </app-quick-start-editor>
            </div>

            @if (localizationVariantGroups.length > 0) {
                <div class="localization-review">
                    @for (variant of localizationVariantGroups; track variant.get('locale')?.value; let variantIndex = $index) {
                        <mat-expansion-panel [formGroup]="variant"
                                             [expanded]="isLocalizationVariantValidationTarget(variantIndex)">
                            <mat-expansion-panel-header>
                                <mat-panel-title>{{ variant.get('locale')?.value }}</mat-panel-title>
                                <mat-panel-description>
                                    {{ variant.get('generatedAt')?.value || 'Manual draft' }}
                                </mat-panel-description>
                            </mat-expansion-panel-header>

                            <div class="localization-grid">
                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>Widget text</mat-label>
                                    <input matInput formControlName="widgetText">
                                </mat-form-field>
                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>Intro text</mat-label>
                                    <input matInput formControlName="introMessage">
                                </mat-form-field>
                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>First message</mat-label>
                                    <textarea matInput rows="2" formControlName="firstMessage"></textarea>
                                </mat-form-field>
                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>Input placeholder</mat-label>
                                    <input matInput formControlName="inputPlaceholder">
                                </mat-form-field>
                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>Dropdown button text</mat-label>
                                    <input matInput formControlName="dropdownBtnText">
                                </mat-form-field>
                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>Dropdown close text</mat-label>
                                    <input matInput formControlName="dropdownBtnTextClose">
                                </mat-form-field>
                                <mat-form-field appearance="fill" class="full-width localization-wide">
                                    <mat-label>Dropdown message</mat-label>
                                    <textarea matInput rows="2" formControlName="dropdownMessage"></textarea>
                                </mat-form-field>
                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>Error messages</mat-label>
                                    <textarea matInput rows="3" formControlName="errorMessages"></textarea>
                                    <mat-hint>One message per line.</mat-hint>
                                </mat-form-field>
                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>Wait messages</mat-label>
                                    <textarea matInput rows="3" formControlName="waitMessages"></textarea>
                                    <mat-hint>One message per line.</mat-hint>
                                </mat-form-field>
                                <app-quick-start-editor class="localization-wide"
                                    [sampleMessages]="getLocalizationSampleMessages(variant)"
                                    [chatbotId]="chatbotId"
                                    [localeLabel]="variant.get('locale')?.value || 'Translation'"
                                    [allowEmpty]="true">
                                </app-quick-start-editor>
                            </div>
                        </mat-expansion-panel>
                    }
                </div>
            }
        </mat-expansion-panel>

        <!-- General Settings -->
        <mat-expansion-panel formGroupName="generalSettings" [expanded]="expandHeaderCapabilitiesPanel">
            <mat-expansion-panel-header>
                <mat-panel-title>
                    Header and Capabilities
                </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="general-settings d-sm-flex flex-column">
                <!-- Name Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Name</mat-label>
                    <input matInput formControlName="name" placeholder="Enter frontend name">
                    @if (frontendForm.get('generalSettings.name')?.invalid && frontendForm.get('generalSettings.name')?.touched) {
                        <mat-error>
                            Name is required
                        </mat-error>
                    }
                </mat-form-field>

                <div class="header-logo-uploader">
                    @if (headerLogoPreview) {
                        <div class="header-logo-preview">
                            <img [src]="headerLogoPreview" alt="Header logo preview">
                        </div>
                    } @else {
                        <div class="header-logo-placeholder">No header logo selected</div>
                    }

                    <div class="header-logo-actions">
                        <button mat-stroked-button color="primary" type="button" (click)="headerLogoFileInput.click()">
                            Upload Logo
                        </button>
                        <input #headerLogoFileInput (change)="onHeaderLogoSelected($event)" accept="image/*,.svg" hidden
                               type="file">
                    </div>
                </div>

                <!-- Logo URL Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Logo URL</mat-label>
                    <input matInput formControlName="logo" placeholder="https://example.com/logo.png">
                    <mat-hint>Paste a hosted URL, SVG data URI, or upload an image above.</mat-hint>
                    @if (frontendForm.get('generalSettings.logo')?.hasError('pattern') && frontendForm.get('generalSettings.logo')?.touched) {
                        <mat-error>
                            Enter a valid URL, SVG data URI, or upload an image.
                        </mat-error>
                    }
                </mat-form-field>

                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Header display</mat-label>
                    <mat-select formControlName="headerDisplayMode">
                        <mat-option value="">Name and logo</mat-option>
                        <mat-option value="logo-only">Logo only</mat-option>
                    </mat-select>
                    <mat-hint>Logo only removes the chatbot name and uses a logo + close header layout.</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Layout preset</mat-label>
                    <mat-select formControlName="layoutPreset">
                        @for (option of layoutPresetOptions; track option) {
                            <mat-option [value]="option.key">{{ option.value }}</mat-option>
                        }
                    </mat-select>
                    <mat-hint>Choose Compact mobile to enable the compact mobile-first layout.</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Logo width (in px)</mat-label>
                    <input matInput formControlName="logoWidth" type="number" placeholder="up to 120px">
                    @if (frontendForm.get('generalSettings.logoWidth')?.invalid && frontendForm.get('generalSettings.logoWidth')?.touched) {
                        <mat-error>
                            Logo width must be a positive number between 0 and 120
                        </mat-error>
                    }
                </mat-form-field>

                <!-- Voice Enabled Toggle -->
                <mat-slide-toggle formControlName="voiceEnabled">Voice Enabled</mat-slide-toggle>
                <mat-slide-toggle formControlName="attachmentsEnabled">File uploads enabled</mat-slide-toggle>

                <div id="image-composition-settings" class="image-composition-settings" [formGroup]="imageCompositionGroup">
                    <div class="image-composition-header">
                        <div>
                            <strong>Visual product matching</strong>
                            <p class="group-hint">
                                Match uploaded clothing photos against product-feed items. Accepted photos are JPEG,
                                PNG, or WebP up to 12 MB before client-side resize/compression to a 2 MB JPEG payload.
                            </p>
                        </div>
                        <mat-slide-toggle formControlName="enabled">
                            Activate visual matching
                        </mat-slide-toggle>
                    </div>

                    <div class="image-composition-note">
                        User photos are checked by the backend safety gate before any vision step. HEIC and HEIF are
                        accepted only when the browser can decode them locally; otherwise the widget rejects them before
                        upload.
                    </div>

                    <mat-slide-toggle formControlName="cameraCaptureEnabled">
                        Show camera capture button
                    </mat-slide-toggle>

                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Eligible categories</mat-label>
                        <textarea
                            formControlName="eligibleCategoriesText"
                            matInput
                            placeholder="Kitchen&#10;Living room&#10;Outdoor"
                            rows="4"></textarea>
                        <mat-hint>
                            Use one category per line or a comma-separated list. Duplicates are removed automatically.
                            Catch-all values such as all or * are not allowed.
                        </mat-hint>
                        @if (imageCompositionValidation.categoryError) {
                            <mat-error>{{ imageCompositionValidation.categoryError }}</mat-error>
                        }
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Consent and disclaimer text</mat-label>
                        <textarea
                            formControlName="disclaimerText"
                            matInput
                            placeholder="Upload a clothing photo to match similar products. Photos are resized in the browser and sent only with the current request."
                            rows="4"></textarea>
                        <mat-hint>
                            This text can be prepared while activation is disabled and is required before activation can
                            be enabled.
                        </mat-hint>
                        @if (imageCompositionValidation.disclaimerError) {
                            <mat-error>{{ imageCompositionValidation.disclaimerError }}</mat-error>
                        }
                    </mat-form-field>

                    @if (imageCompositionValidation.activationErrors.length > 0) {
                        <div class="image-composition-errors">
                            @for (error of imageCompositionValidation.activationErrors; track error) {
                                <div class="error-text">{{ error }}</div>
                            }
                        </div>
                    }
                </div>
            </div>
        </mat-expansion-panel>
        <mat-expansion-panel formGroupName="generalSettings" [expanded]="expandOpeningMessagesPanel">
            <mat-expansion-panel-header>
                <mat-panel-title>
                    Opening Messages
                </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="input-settings d-sm-flex flex-column">
                <!-- Name Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Intro text</mat-label>
                    <input matInput formControlName="introMessage" placeholder="">
                </mat-form-field>
            </div>
            <div class="input-settings d-sm-flex flex-column">
                <!-- Name Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>First message</mat-label>
                    <input matInput formControlName="firstMessage" placeholder="">
                </mat-form-field>
            </div>
            <div class="input-settings d-sm-flex flex-column">
                <!-- Name Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Input field placeholder</mat-label>
                    <input matInput formControlName="inputPlaceholder" placeholder="">
                </mat-form-field>
                <div class="basics-grid">
                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Send icon</mat-label>
                        <mat-select formControlName="submitButtonIcon">
                            @for (option of materialIconOptions; track option) {
                                <mat-option [value]="option.key">{{ option.value }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Reset icon</mat-label>
                        <mat-select formControlName="resetButtonIcon">
                            @for (option of materialIconOptions; track option) {
                                <mat-option [value]="option.key">{{ option.value }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Compact send icon</mat-label>
                        <mat-select formControlName="compactSubmitButtonIcon">
                            @for (option of materialIconOptions; track option) {
                                <mat-option [value]="option.key">{{ option.value }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Compact reset icon</mat-label>
                        <mat-select formControlName="compactResetButtonIcon">
                            @for (option of materialIconOptions; track option) {
                                <mat-option [value]="option.key">{{ option.value }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Attach icon</mat-label>
                        <mat-select formControlName="attachButtonIcon">
                            @for (option of materialIconOptions; track option) {
                                <mat-option [value]="option.key">{{ option.value }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Voice icon</mat-label>
                        <mat-select formControlName="voiceButtonIcon">
                            @for (option of materialIconOptions; track option) {
                                <mat-option [value]="option.key">{{ option.value }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                </div>
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Reset help text</mat-label>
                    <input matInput formControlName="resetButtonTooltip" placeholder="Click to reset your chat">
                </mat-form-field>
                <mat-slide-toggle formControlName="resetButtonConfirmationEnabled">
                    Require reset confirmation click
                </mat-slide-toggle>
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Reset confirmation text</mat-label>
                    <input matInput formControlName="resetButtonConfirmationText" placeholder="Click again to reset your chat">
                </mat-form-field>
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Quick-start title</mat-label>
                    <input matInput formControlName="sampleMessagesTitle" placeholder="Quick start">
                </mat-form-field>
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Quick-start presentation</mat-label>
                    <mat-select formControlName="sampleMessagesPresentation">
                        @for (option of sampleMessagesPresentationOptions; track option) {
                            <mat-option [value]="option.key">{{ option.value }}</mat-option>
                        }
                    </mat-select>
                </mat-form-field>
                <app-color-field
                    inputId="sampleMessagesHoverBorderColor"
                    label="Quick-start hover border color"
                    hint="Border color when a quick-start suggestion is hovered."
                    placeholder="#121413"
                    [control]="getGeneralSettingsControl('sampleMessagesHoverBorderColor')">
                </app-color-field>
                <mat-slide-toggle formControlName="sampleMessagesClickAnimationEnabled">
                    Enable quick-start click animation
                </mat-slide-toggle>
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Quick-start click animation delay (ms)</mat-label>
                    <input matInput type="number" min="0" max="600" formControlName="sampleMessagesClickAnimationDelayMs">
                </mat-form-field>
            </div>
        </mat-expansion-panel>
        <app-messages-settings [messagesForm]="messagesForm"></app-messages-settings>
        <!-- Product Card Settings -->
        <mat-expansion-panel formGroupName="productCardSettings" [expanded]="expandProductCardsPanel || expandProductCardsValidationPanel">
            <mat-expansion-panel-header>
                <mat-panel-title>
                    Product Cards
                </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="product-card-enable d-sm-flex flex-column" [formGroup]="settingsForm">
                <mat-slide-toggle [formControl]="aiProductCardsEnabledControl">
                    Enable AI product cards
                </mat-slide-toggle>
                <p class="group-hint">
                    Controls only the built-in AI-generated path. Governed product-feed, configured function, and
                    workflow cards continue to use the presentation and styling controls below.
                </p>

                @if (aiProductCardsEnabledControl.value) {
                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>AI product card instruction appendix</mat-label>
                        <textarea
                            matInput
                            rows="5"
                            [formControl]="aiProductCardsInstructionControl"
                            placeholder="Optional extra guidance for when the assistant should or should not render product cards."></textarea>
                        <mat-hint>
                            Appended to the built-in product-card tool guidance for the assistant.
                        </mat-hint>
                    </mat-form-field>
                }
            </div>

            <div class="product-card-settings d-sm-flex flex-column">
                <!-- Card Type Selection -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Card Type</mat-label>
                    <mat-select formControlName="cardType" placeholder="Select card type">
                        @for (option of cardTypeOptions; track option) {
                            <mat-option [value]="option.key">{{ option.value }}</mat-option>
                        }
                    </mat-select>
                    @if (frontendForm.get('productCardSettings.cardType')?.invalid && frontendForm.get('productCardSettings.cardType')?.touched) {
                        <mat-error>
                            Card Type is required
                        </mat-error>
                    }
                </mat-form-field>

                <!-- Product Card Presentation -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Card Presentation</mat-label>
                    <mat-select formControlName="productCardPresentation" placeholder="Select card presentation">
                        @for (option of productCardPresentationOptions; track option) {
                            <mat-option [value]="option.key">{{ option.value }}</mat-option>
                        }
                    </mat-select>
                    <mat-hint>This is the fixed choice, or the deterministic default in dynamic mode.</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Presentation policy</mat-label>
                    <mat-select formControlName="productCardPresentationMode">
                        <mat-option value="fixed">Fixed — operator choice always wins</mat-option>
                        <mat-option value="dynamic">Bounded dynamic — AI/functions may choose from an allowlist</mat-option>
                    </mat-select>
                    <mat-hint>Fixed is the safe default. An instruction appendix never overrides a fixed choice.</mat-hint>
                </mat-form-field>

                @if (productCardPresentationMode === 'dynamic') {
                    <mat-form-field appearance="fill" class="full-width">
                        <mat-label>Allowed dynamic presentations</mat-label>
                        <mat-select formControlName="productCardDynamicPresentations" multiple>
                            @for (option of productCardPresentationOptions; track option.key) {
                                <mat-option [value]="option.key">{{ option.value }}</mat-option>
                            }
                        </mat-select>
                        <mat-hint>An empty allowlist disables dynamic overrides and falls back to Card Presentation.</mat-hint>
                    </mat-form-field>
                }

                <div class="product-card-policy-preview" role="status" aria-live="polite">
                    <strong>Runtime preview:</strong>
                    @if (productCardPresentationMode === 'dynamic') {
                        Bounded dynamic; invalid or disallowed choices fall back to
                        {{ frontendForm.get('productCardSettings.productCardPresentation')?.value || 'horizontal' }}.
                    } @else {
                        Fixed {{ frontendForm.get('productCardSettings.productCardPresentation')?.value || 'horizontal' }};
                        AI and functions cannot override it.
                    }
                </div>

                <section class="product-card-runtime-preview" aria-labelledby="product-card-preview-title">
                    <div class="product-card-runtime-preview__header">
                        <div>
                            <strong id="product-card-preview-title">Governed card preview</strong>
                            @if (figmaStylingMode === 'experimental') {
                                <p>Samme Figma-design og eksempelbilder som i Chat Window Styling, med knappetekstene fra dette panelet.</p>
                            } @else {
                                <p>The hosted widget's real retail and automotive renderer, presentation classes, responsive styles, and current custom CSS.</p>
                            }
                        </div>
                        @if (figmaStylingMode === 'experimental') {
                            <mat-form-field appearance="outline">
                                <mat-label>Figma-visning</mat-label>
                                <mat-select [value]="productCardFigmaView" (selectionChange)="selectProductCardFigmaView($event.value)">
                                    @for (view of productCardFigmaViews; track view.name) {
                                        <mat-option [value]="view.name">{{ view.name }}</mat-option>
                                    }
                                </mat-select>
                            </mat-form-field>
                        } @else {
                            <span>{{ productCardPreviewPresentation }}</span>
                        }
                    </div>
                    <div class="product-card-runtime-preview__scroll">
                    <div class="design-preview-viewport" tabindex="0" role="region" aria-label="Produktkortforhåndsvisning – kan rulles vannrett">
                    <iframe
                        #productCardPreviewFrame
                        class="product-card-runtime-preview__frame"
                        [src]="productCardPreviewUrl"
                        [style.width.px]="productCardPreviewWidth"
                        [style.height.px]="productCardPreviewHeight"
                        title="Retail and automotive product-card preview"
                        sandbox="allow-scripts allow-same-origin"
                        referrerpolicy="no-referrer"
                        (load)="onProductCardPreviewLoad(productCardPreviewFrame)"></iframe>
                    </div>
                    </div>
                    <p class="product-card-runtime-preview__status" role="status" aria-live="polite">
                        {{ productCardPreviewStatus }}
                    </p>
                </section>

                <mat-slide-toggle formControlName="addToCartEnabled">
                    Show Add to cart button
                </mat-slide-toggle>
                <p class="group-hint">
                    Sends product data to the host page with window.shineAddToCart or the shine:add-to-cart event.
                </p>

                <mat-slide-toggle formControlName="productCardEcommerceTrackingEnabled">
                    Enable first-party product-card analytics
                </mat-slide-toggle>
                <p class="group-hint">
                    Records consent-safe displayed, viewed, hovered, clicked, saved, and added-to-cart events with governed identity. No raw personal data is stored.
                </p>

                <mat-slide-toggle formControlName="ga4EcommerceEventsEnabled">
                    Emit GA4 ecommerce events
                </mat-slide-toggle>
                <p class="group-hint">
                    Pushes standard GA4 ecommerce events for Shine AI product-card impressions, selections, and add-to-cart actions.
                </p>
                @if (frontendForm.get('productCardSettings.ga4EcommerceEventsEnabled')?.value) {
                    <div class="ga4-setup-note">
                        <h4>GA4 Admin setup</h4>
                        <p>
                            Create event-scoped custom dimensions in GA4 Admin for chatbot ecommerce filtering.
                            Use <code>shine_event_source</code> as the required dimension and filter for
                            <code>chatbot</code>.
                        </p>
                        <p>
                            Optional event-scoped dimensions: <code>shine_surface</code>,
                            <code>shine_chatbot_id</code>, and <code>shine_client_id</code>.
                        </p>
                        <a
                            href="https://support.google.com/analytics/answer/14239696?hl=en"
                            target="_blank"
                            rel="noopener noreferrer">
                            Google Analytics custom dimensions guide
                        </a>
                    </div>
                }

                <!-- Card Link URL -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Card Link URL</mat-label>
                    <input matInput formControlName="cardLinkUrl" placeholder="https://example.com/product" type="url">
                    @if (frontendForm.get('productCardSettings.cardLinkUrl')?.hasError('pattern') && frontendForm.get('productCardSettings.cardLinkUrl')?.touched) {
                        <mat-error>
                            Enter a valid URL (must start with http:// or https://)
                        </mat-error>
                    }
                    <mat-hint>Legacy cards only. Governed v1 cards keep their authoritative destination URL.</mat-hint>
                </mat-form-field>

                <!-- Card UTM String -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Card UTM String</mat-label>
                    <input matInput formControlName="cardUtmString" placeholder="utm_source=chatbot&utm_medium=widget">
                    @if (frontendForm.get('productCardSettings.cardUtmString')?.invalid && frontendForm.get('productCardSettings.cardUtmString')?.touched) {
                        <mat-error>Use only utm_ key/value pairs separated by &amp;.</mat-error>
                    }
                    <mat-hint>Legacy cards only. Governed v1 URLs are preserved exactly without appended tracking parameters.</mat-hint>
                </mat-form-field>

                <!-- Card Locale -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Card Locale</mat-label>
                    <mat-select formControlName="cardLocale" placeholder="Select card locale">
                        @for (option of localeOptions; track option) {
                            <mat-option [value]="option.value">{{ option.display }}</mat-option>
                        }
                    </mat-select>
                    @if (frontendForm.get('productCardSettings.cardLocale')?.invalid && frontendForm.get('productCardSettings.cardLocale')?.touched) {
                        <mat-error>
                            Card Locale is required
                        </mat-error>
                    }
                </mat-form-field>

                <!-- Card Currency -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Card Currency</mat-label>
                    <input matInput formControlName="cardCurrency" placeholder="USD">
                    @if (frontendForm.get('productCardSettings.cardCurrency')?.invalid && frontendForm.get('productCardSettings.cardCurrency')?.touched) {
                        <mat-error>Use a three-letter uppercase currency code, for example NOK.</mat-error>
                    }
                    <mat-hint>Brukes til prisvisningen når produktet mangler valuta. Produktets egen valuta har forrang.</mat-hint>
                </mat-form-field>
                @if (productCardTextOverridesControl; as textControl) {
                    <app-product-card-text-settings
                        [control]="textControl"
                        [locale]="productCardTextLocale"
                        [localeLabel]="productCardTextLocaleLabel"
                        [defaults]="productCardDefaultTexts">
                    </app-product-card-text-settings>
                }
            </div>
        </mat-expansion-panel>

        <!-- Dropdown Settings -->
        <mat-expansion-panel formGroupName="dropdownSettings">
            <mat-expansion-panel-header>
                <mat-panel-title>
                    Saved Items Dropdown
                </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="dropdown-settings d-sm-flex flex-column">
                <!-- Dropdown Enabled Toggle -->
                <mat-slide-toggle formControlName="dropdownEnabled">Dropdown Enabled</mat-slide-toggle>

                <!-- Dropdown Button Text -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Dropdown Button Text</mat-label>
                    <input matInput formControlName="dropdownBtnText" placeholder="Options">
                </mat-form-field>

                <!-- Dropdown Button Text Close -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Dropdown Button Text (Close)</mat-label>
                    <input matInput formControlName="dropdownBtnTextClose" placeholder="Close">
                </mat-form-field>

                <!-- Dropdown Message -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Dropdown Message</mat-label>
                    <textarea formControlName="dropdownMessage" matInput placeholder="Select an option..."
                              rows="3"></textarea>
                </mat-form-field>

                <!-- Dropdown Button Icon Enabled Toggle -->
                <mat-slide-toggle formControlName="dropdownBtnIconEnabled">Dropdown Button Icon Enabled
                </mat-slide-toggle>
            </div>
        </mat-expansion-panel>

        <!-- Chat Avatar -->
        <mat-expansion-panel [expanded]="expandAssistantAvatarPanel">
            <mat-expansion-panel-header>
                <mat-panel-title>
                    Assistant Avatar
                </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="chat-avatar d-sm-flex flex-column">
                <mat-slide-toggle formControlName="chatAvatarEnabled">Show Icon Before AI Messages</mat-slide-toggle>

                @if (chatAvatarPreview) {
                    <div class="chat-avatar-preview">
                        <img [src]="chatAvatarPreview" alt="Chat avatar preview">
                    </div>
                } @else {
                    <div class="chat-avatar-placeholder">No avatar selected</div>
                }

                <div class="chat-avatar-actions">
                    <button mat-stroked-button color="primary" type="button" (click)="chatAvatarFileInput.click()">
                        Upload Image
                    </button>
                    <input #chatAvatarFileInput (change)="onChatAvatarSelected($event)" accept="image/*,.svg" hidden
                           type="file">
                </div>

                <!-- Chat Avatar URL Field -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Chat Avatar URL</mat-label>
                    <input formControlName="chatAvatar" matInput placeholder="https://example.com/avatar.png"
                           type="url">
                    <mat-hint>Used when no image is uploaded.</mat-hint>
                    @if (frontendForm.get('chatAvatar')?.hasError('pattern') && frontendForm.get('chatAvatar')?.touched) {
                        <mat-error>
                            Enter a valid URL (must start with http:// or https://)
                        </mat-error>
                    }
                </mat-form-field>
            </div>
        </mat-expansion-panel>

        <!-- Styling -->
        <mat-expansion-panel [expanded]="expandStylingPanel">
            <mat-expansion-panel-header>
                <mat-panel-title>
                    Chat Window Styling
                </mat-panel-title>
            </mat-expansion-panel-header>

            <app-figma-product-card-settings [clientId]="clientId" [chatbotId]="chatbotId"
                (modeChange)="onFigmaModeChange($event)" (snapshotChange)="onFigmaSnapshotChange($event)"></app-figma-product-card-settings>

            @if (figmaStylingMode === 'standard') {
            <div class="styling-wrapper" formGroupName="styling">
                <div class="styling-basics">
                    <div class="basics-grid">
                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>Chatbot Width (px)</mat-label>
                            <input matInput formControlName="chatBotWidth" placeholder="e.g. 1200">
                            @if (frontendForm.get('styling.chatBotWidth')?.invalid && frontendForm.get('styling.chatBotWidth')?.touched) {
                                <mat-error>
                                    Chatbot width must contain only numbers.
                                </mat-error>
                            }
                        </mat-form-field>

                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>Font source</mat-label>
                            <mat-select formControlName="fontProvider" placeholder="Select font source">
                                @for (provider of fontProviderOptions; track provider) {
                                    <mat-option [value]="provider.value">{{ provider.key }}</mat-option>
                                }
                            </mat-select>
                        </mat-form-field>

                        @if (frontendForm.get('styling.fontProvider')?.value === 'google' || !frontendForm.get('styling.fontProvider')?.value) {
                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Google font</mat-label>
                                <mat-select formControlName="font" placeholder="Select font">
                                    @for (font of fontOptions; track font) {
                                        <mat-option [value]="font.value">{{ font.key }}</mat-option>
                                    }
                                </mat-select>
                            </mat-form-field>
                        }

                        @if (frontendForm.get('styling.fontProvider')?.value === 'custom') {
                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Custom font preset</mat-label>
                                <mat-select formControlName="fontPreset" placeholder="Select preset">
                                    @for (preset of customFontPresetOptions; track preset) {
                                        <mat-option [value]="preset.value">{{ preset.key }}</mat-option>
                                    }
                                </mat-select>
                                <mat-hint>Choose a preset, or leave None selected and provide hosted font files below.</mat-hint>
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Font family</mat-label>
                                <input matInput formControlName="fontFamily" placeholder="Haffer SQ">
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Fallback stack</mat-label>
                                <input matInput formControlName="fontFallback" placeholder="Inter, Arial, sans-serif">
                            </mat-form-field>

                            @if (!frontendForm.get('styling.fontPreset')?.value) {
                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>Regular font URL</mat-label>
                                    <input matInput formControlName="fontFaceRegularUrl" placeholder="https://cdn.example.com/font-regular.woff2">
                                    <mat-hint>Required. For preview and cross-origin embeds, the font host must allow anonymous CORS requests.</mat-hint>
                                </mat-form-field>

                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>Medium font URL</mat-label>
                                    <input matInput formControlName="fontFaceMediumUrl" placeholder="https://cdn.example.com/font-medium.woff2">
                                </mat-form-field>

                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>Semi-bold font URL</mat-label>
                                    <input matInput formControlName="fontFaceSemiBoldUrl" placeholder="https://cdn.example.com/font-semibold.woff2">
                                </mat-form-field>

                                <mat-form-field appearance="fill" class="full-width">
                                    <mat-label>Bold font URL</mat-label>
                                    <input matInput formControlName="fontFaceBoldUrl" placeholder="https://cdn.example.com/font-bold.woff2">
                                </mat-form-field>

                                @if (customFontConfigurationValidation.message) {
                                    <p class="font-configuration-error" role="alert">
                                        {{ customFontConfigurationValidation.message }}
                                    </p>
                                }
                            }
                        }

                        @if (frontendForm.get('styling.fontProvider')?.value === 'system') {
                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>System font family</mat-label>
                                <input matInput formControlName="fontFamily" placeholder="Arial">
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Fallback stack</mat-label>
                                <input matInput formControlName="fontFallback" placeholder="system-ui, Arial, sans-serif">
                            </mat-form-field>
                        }
                    </div>
                </div>

                <mat-card class="product-card-layout-card">
                    <mat-card-title>Chat Bubble Layout</mat-card-title>
                    <div class="basics-grid">
                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>Assistant radius (desktop)</mat-label>
                            <input formControlName="chatBubbleAssistantBorderRadiusDesktop" matInput
                                   placeholder="12px 12px 12px 0">
                        </mat-form-field>

                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>Assistant radius (mobile)</mat-label>
                            <input formControlName="chatBubbleAssistantBorderRadiusMobile" matInput
                                   placeholder="12px 12px 12px 0">
                        </mat-form-field>

                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>Assistant text size (desktop)</mat-label>
                            <input formControlName="chatBubbleAssistantFontSizeDesktop" matInput placeholder="16px">
                        </mat-form-field>

                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>Assistant text size (mobile)</mat-label>
                            <input formControlName="chatBubbleAssistantFontSizeMobile" matInput placeholder="14px">
                        </mat-form-field>

                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>User radius</mat-label>
                            <input formControlName="chatBubbleUserBorderRadius" matInput placeholder="12px 12px 0 12px">
                        </mat-form-field>

                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>Assistant icon size (desktop)</mat-label>
                            <input formControlName="chatAvatarSizeDesktop" matInput placeholder="50px">
                        </mat-form-field>

                        <mat-form-field appearance="fill" class="full-width">
                            <mat-label>Assistant icon size (mobile)</mat-label>
                            <input formControlName="chatAvatarSizeMobile" matInput placeholder="16px">
                        </mat-form-field>
                    </div>
                </mat-card>

                @if (aiProductCardsEnabled || frontendForm.get('productCardSettings.cardType')?.value) {
                    <mat-card class="product-card-layout-card">
                        <mat-card-title>Product Card Layout</mat-card-title>
                        <div class="basics-grid">
                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Dynamic card width</mat-label>
                                <input matInput formControlName="cardWidth" placeholder="320px">
                                <mat-hint>Optional. Applies to rendered horizontal and carousel cards.</mat-hint>
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Card max width</mat-label>
                                <input matInput formControlName="cardMaxWidth" placeholder="273px">
                                <mat-hint>Recommended: 273px. Runtime clamps to fit mobile viewports.</mat-hint>
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Image aspect ratio</mat-label>
                                <input matInput formControlName="cardImageAspectRatio" placeholder="3:4">
                                <mat-hint>KT default: 3:4.</mat-hint>
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Compact image max height</mat-label>
                                <input matInput formControlName="cardCompactImageMaxHeight" placeholder="92px">
                                <mat-hint>Optional. Empty means no max-height.</mat-hint>
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Card padding</mat-label>
                                <input matInput formControlName="cardPadding" placeholder="1rem 0 0">
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Border radius</mat-label>
                                <input matInput formControlName="cardBorderRadius" placeholder="8px">
                            </mat-form-field>

                            <app-color-field
                                inputId="card-border-color"
                                label="Card border color"
                                hint="Leave blank for the runtime default, or use transparent to remove the outline."
                                placeholder="transparent or #111827"
                                [control]="getStylingControl('cardBorderColor')"
                                [supportsTransparent]="true">
                            </app-color-field>

                            <app-color-field
                                inputId="card-background-color"
                                label="Card background color"
                                hint="Leave blank for the runtime default, or use transparent to keep the card clear."
                                placeholder="transparent or #FFFFFF"
                                [control]="getStylingControl('cardBackgroundColor')"
                                [supportsTransparent]="true">
                            </app-color-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Title font size</mat-label>
                                <input matInput formControlName="cardTitleFontSize" placeholder="19px">
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Title font weight</mat-label>
                                <input matInput formControlName="cardTitleFontWeight" placeholder="600">
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Description font size</mat-label>
                                <input matInput formControlName="cardDescriptionFontSize" placeholder="15px">
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Price font size</mat-label>
                                <input matInput formControlName="cardPriceFontSize" placeholder="17px">
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Price font weight</mat-label>
                                <input matInput formControlName="cardPriceFontWeight" placeholder="700">
                            </mat-form-field>

                            <mat-form-field appearance="fill" class="full-width">
                                <mat-label>Original price font size</mat-label>
                                <input matInput formControlName="cardPriceOriginalFontSize" placeholder="15px">
                            </mat-form-field>
                        </div>
                        <div class="toggle-row">
                            <mat-slide-toggle formControlName="cardDescriptionVisible">Show description</mat-slide-toggle>
                            <mat-slide-toggle formControlName="cardCtaVisible">Show button</mat-slide-toggle>
                            <mat-slide-toggle formControlName="cardPriceVisible">Show price</mat-slide-toggle>
                        </div>
                    </mat-card>
                }

                <div class="toggle-row composer-toggle-row">
                    <mat-slide-toggle formControlName="inputShadowEnabled">Show input shadow</mat-slide-toggle>
                    <span class="group-hint">Adds depth around the message field. Turn it off for a flat composer.</span>
                </div>

                <div class="color-groups">
                    @for (group of colorGroups; track group) {
                        @if (isGroupVisible(group)) {
                            <mat-card [class.group-disabled]="isAdvancedSectionDisabled(group)"
                                      [class.requires-advanced]="group.requiresAdvanced">
                                <mat-card-title>{{ group.title }}</mat-card-title>
                                <div class="color-grid">
                                    @for (control of group.controls; track control) {
                                        @if (isControlVisible(control)) {
                                            <app-color-field
                                                [control]="getStylingControl(control.key)"
                                                [disabled]="isControlDisabled(control, group)"
                                                [hint]="control.hint || ''"
                                                [inputId]="control.key"
                                                [label]="control.label"
                                                [placeholder]="'#FFFFFF'">
                                            </app-color-field>
                                            @if (control.hover) {
                                                <app-color-field
                                                    [control]="getStylingControl(control.hover.key)"
                                                    [disabled]="isControlDisabled(control, group)"
                                                    [hint]="control.hover.hint || control.hint || ''"
                                                    [inputId]="control.hover.key"
                                                    [label]="control.hover.label || (control.label + ' hover')"
                                                    [placeholder]="'#FFFFFF'">
                                                </app-color-field>
                                            }
                                        }
                                    }
                                </div>
                            </mat-card>
                        }
                    }
                </div>
            </div>
            }
        </mat-expansion-panel>

        <!-- Global CSS and JS -->
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    Custom CSS and JS
                </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="global-css-js d-sm-flex flex-column">
                <!-- Global CSS -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Global CSS</mat-label>
                    <textarea formControlName="css" matInput placeholder="/* Your global CSS here */"
                              rows="5"></textarea>
                    <mat-hint>Product cards retain legacy selectors and add .product-card--contract-v1 for governed styling.</mat-hint>
                    @if (frontendForm.get('css')?.hasError('maxlength')) {
                        <mat-error>Custom CSS must be 50,000 characters or fewer.</mat-error>
                    }
                </mat-form-field>
                @if (productCardCssCollisionSelectors.length > 0) {
                    <div class="product-card-css-warning" role="alert">
                        Custom CSS targets product-card selectors: {{ productCardCssCollisionSelectors.join(', ') }}.
                        Verify the card preview and beta journeys before publishing; scope new rules under
                        <code>.product-card--contract-v1</code> when they should apply only to governed cards.
                    </div>
                }

                <!-- Global JS -->
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Global JS</mat-label>
                    <textarea formControlName="js" matInput placeholder="// Your global JavaScript here"
                              rows="5"></textarea>
                </mat-form-field>
            </div>
        </mat-expansion-panel>

    </mat-accordion>

</div>
